// Human-in-the-loop review panel.
//
// Renders one row per metric (plus an `overall` row). Each row has
// thumbs-up/down/unsure buttons + a notes textarea. The `dispo_alignment`
// row additionally shows a corrected-dispo dropdown, enabled only when the
// reviewer thumb-downs.
//
// Optimistic UI: clicking a thumb immediately flips the local state and
// fires the POST; if the server 4xx/5xx's the mutation onError handler
// reverts.
//
// Existing reviews are loaded on mount and pre-populate the row state. The
// server upserts on (call_id, metric, reviewer) so re-submitting the same
// row replaces the prior verdict/notes.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Check, HelpCircle, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import type { Review, ReviewMetric, ReviewSubmission, ReviewVerdict } from '../types'

const REVIEW_ROWS: Array<{ metric: ReviewMetric; label: string }> = [
  { metric: 'talk_ratio', label: 'Talk ratio' },
  { metric: 'longest_monologue', label: 'Longest monologue' },
  { metric: 'next_step_commitment', label: 'Next-step commitment' },
  { metric: 'objection_delta', label: 'Objection handling' },
  { metric: 'dispo_alignment', label: 'Dispo alignment' },
  { metric: 'overall', label: 'Overall call' },
]

const KNOWN_DISPOS = [
  'HU',
  'A',
  'NI',
  'N',
  'WRNGNO',
  'DNC',
  'Renter',
  'DNQ',
  'NR',
  'CALLBK',
  'SALE',
  'OTHER',
]

const REVIEWER_STORAGE_KEY = 'killacoach_reviewer'

function getStoredReviewer(): string {
  try {
    return localStorage.getItem(REVIEWER_STORAGE_KEY) ?? ''
  } catch {
    return ''
  }
}

function setStoredReviewer(v: string) {
  try {
    localStorage.setItem(REVIEWER_STORAGE_KEY, v)
  } catch {
    /* private mode */
  }
}

interface RowState {
  verdict: ReviewVerdict | null
  corrected_dispo: string | null
  notes: string
  dirty: boolean
  savedId: number | null
}

const EMPTY_ROW: RowState = {
  verdict: null,
  corrected_dispo: null,
  notes: '',
  dirty: false,
  savedId: null,
}

export function ReviewPanel({
  callId,
  passedByMetric,
  predictedDispo,
}: {
  callId: number
  passedByMetric?: Partial<Record<ReviewMetric, boolean | null>>
  predictedDispo?: string | null
}) {
  const qc = useQueryClient()
  const [reviewer, setReviewer] = useState<string>(getStoredReviewer)
  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const init: Record<string, RowState> = {}
    for (const r of REVIEW_ROWS) init[r.metric] = { ...EMPTY_ROW }
    return init
  })

  const { data } = useQuery({
    queryKey: ['reviews', callId],
    queryFn: () => api.reviews.list(callId),
    enabled: Number.isFinite(callId),
  })

  // Hydrate row state from existing reviews for THIS reviewer. If reviewer is
  // blank we pre-fill from the null-reviewer row if any exists.
  useEffect(() => {
    if (!data) return
    const next: Record<string, RowState> = {}
    for (const r of REVIEW_ROWS) next[r.metric] = { ...EMPTY_ROW }
    const want = reviewer.trim() || null
    for (const rv of data.reviews) {
      if ((rv.reviewer ?? null) !== want) continue
      if (!(rv.metric in next)) continue
      next[rv.metric] = {
        verdict: rv.verdict,
        corrected_dispo: rv.corrected_dispo,
        notes: rv.notes ?? '',
        dirty: false,
        savedId: rv.id,
      }
    }
    setRows(next)
  }, [data, reviewer])

  const submit = useMutation({
    mutationFn: (payload: ReviewSubmission) => api.reviews.submit(callId, payload),
    onSuccess: (saved: Review) => {
      setRows((prev) => ({
        ...prev,
        [saved.metric]: {
          verdict: saved.verdict,
          corrected_dispo: saved.corrected_dispo,
          notes: saved.notes ?? '',
          dirty: false,
          savedId: saved.id,
        },
      }))
      void qc.invalidateQueries({ queryKey: ['reviews', callId] })
    },
    onError: () => {
      // Revert dirty flag so user can retry; reload canonical state from server.
      void qc.invalidateQueries({ queryKey: ['reviews', callId] })
    },
  })

  const handleVerdict = (metric: ReviewMetric, verdict: ReviewVerdict) => {
    const prev = rows[metric] ?? EMPTY_ROW
    // Optimistic local flip.
    setRows((s) => ({
      ...s,
      [metric]: { ...prev, verdict, dirty: true },
    }))
    const payload: ReviewSubmission = {
      metric,
      verdict,
      notes: prev.notes || null,
      reviewer: reviewer.trim() || null,
    }
    if (metric === 'dispo_alignment' && verdict === 'disagree') {
      payload.corrected_dispo = prev.corrected_dispo || null
    }
    submit.mutate(payload)
  }

  const handleDispoChange = (value: string) => {
    const prev = rows.dispo_alignment ?? EMPTY_ROW
    setRows((s) => ({
      ...s,
      dispo_alignment: { ...prev, corrected_dispo: value || null, dirty: true },
    }))
    if (prev.verdict === 'disagree') {
      submit.mutate({
        metric: 'dispo_alignment',
        verdict: 'disagree',
        corrected_dispo: value || null,
        notes: prev.notes || null,
        reviewer: reviewer.trim() || null,
      })
    }
  }

  const handleNotesBlur = (metric: ReviewMetric) => {
    const row = rows[metric]
    if (!row || !row.verdict || !row.dirty) return
    const payload: ReviewSubmission = {
      metric,
      verdict: row.verdict,
      notes: row.notes || null,
      reviewer: reviewer.trim() || null,
    }
    if (metric === 'dispo_alignment' && row.verdict === 'disagree') {
      payload.corrected_dispo = row.corrected_dispo || null
    }
    submit.mutate(payload)
  }

  const handleReviewerBlur = () => {
    setStoredReviewer(reviewer.trim())
  }

  const anyError = submit.isError

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <header className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-200">Manager review</h2>
          <p className="mt-0.5 text-[11px] text-slate-500">
            Ground truth for the scoring model. Thumbs-up = model was right. Thumbs-down + correction = training signal.
          </p>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-slate-400">
          <span>Reviewer</span>
          <input
            type="text"
            value={reviewer}
            onChange={(e) => setReviewer(e.target.value)}
            onBlur={handleReviewerBlur}
            placeholder="your initials / vici user"
            className="w-36 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200 focus:border-indigo-600 focus:outline-none"
          />
        </label>
      </header>

      {anyError && (
        <div className="mb-3 flex items-start gap-2 rounded border border-red-900 bg-red-950/40 p-2 text-xs text-red-200">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5" />
          <span>Save failed -- reverted to last server state. Try again.</span>
        </div>
      )}

      <div className="divide-y divide-slate-800">
        {REVIEW_ROWS.map(({ metric, label }) => {
          const row = rows[metric] ?? EMPTY_ROW
          const pass = passedByMetric ? passedByMetric[metric] : null
          return (
            <ReviewRow
              key={metric}
              metric={metric}
              label={label}
              passed={pass ?? null}
              predictedDispo={metric === 'dispo_alignment' ? predictedDispo ?? null : null}
              row={row}
              onVerdict={(v) => handleVerdict(metric, v)}
              onDispoChange={handleDispoChange}
              onNotesChange={(notes) =>
                setRows((s) => ({
                  ...s,
                  [metric]: { ...(s[metric] ?? EMPTY_ROW), notes, dirty: true },
                }))
              }
              onNotesBlur={() => handleNotesBlur(metric)}
            />
          )
        })}
      </div>
    </section>
  )
}

function ReviewRow({
  metric,
  label,
  passed,
  predictedDispo,
  row,
  onVerdict,
  onDispoChange,
  onNotesChange,
  onNotesBlur,
}: {
  metric: ReviewMetric
  label: string
  passed: boolean | null
  predictedDispo: string | null
  row: RowState
  onVerdict: (v: ReviewVerdict) => void
  onDispoChange: (v: string) => void
  onNotesChange: (v: string) => void
  onNotesBlur: () => void
}) {
  const [expanded, setExpanded] = useState<boolean>(Boolean(row.notes))

  const showDispoDropdown = metric === 'dispo_alignment'
  const dropdownEnabled = showDispoDropdown && row.verdict === 'disagree'

  const badgeCls = useMemo(() => {
    if (passed === true) return 'border-emerald-700 bg-emerald-950/50 text-emerald-300'
    if (passed === false) return 'border-red-800 bg-red-950/40 text-red-300'
    return 'border-slate-700 bg-slate-900 text-slate-400'
  }, [passed])

  return (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-start md:gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-slate-200">{label}</span>
          <span
            className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase ${badgeCls}`}
          >
            {passed === true ? 'pass' : passed === false ? 'fail' : '--'}
          </span>
          {showDispoDropdown && predictedDispo && (
            <span className="text-[10px] text-slate-500">
              predicted: <span className="font-mono text-slate-300">{predictedDispo}</span>
            </span>
          )}
        </div>

        {showDispoDropdown && (
          <select
            value={row.corrected_dispo ?? ''}
            onChange={(e) => onDispoChange(e.target.value)}
            disabled={!dropdownEnabled}
            className={`w-40 rounded border px-2 py-1 text-xs ${
              dropdownEnabled
                ? 'border-amber-800 bg-amber-950/40 text-amber-200'
                : 'border-slate-800 bg-slate-950/60 text-slate-600'
            }`}
            title={
              dropdownEnabled
                ? 'Pick the correct dispo'
                : 'Click thumbs-down to enable'
            }
          >
            <option value="">corrected dispo...</option>
            {KNOWN_DISPOS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        )}

        <textarea
          value={row.notes}
          onChange={(e) => onNotesChange(e.target.value)}
          onFocus={() => setExpanded(true)}
          onBlur={() => {
            if (!row.notes) setExpanded(false)
            onNotesBlur()
          }}
          placeholder="Notes (optional)"
          rows={expanded ? 3 : 1}
          className="mt-1 w-full resize-none rounded border border-slate-800 bg-slate-950/60 px-2 py-1 text-xs text-slate-200 transition-[height] focus:border-indigo-700 focus:outline-none"
        />
      </div>

      <div className="flex items-center gap-1">
        <VerdictButton
          active={row.verdict === 'agree'}
          onClick={() => onVerdict('agree')}
          title="Agree with prediction"
          intent="agree"
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </VerdictButton>
        <VerdictButton
          active={row.verdict === 'disagree'}
          onClick={() => onVerdict('disagree')}
          title="Disagree with prediction"
          intent="disagree"
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </VerdictButton>
        <VerdictButton
          active={row.verdict === 'unsure'}
          onClick={() => onVerdict('unsure')}
          title="Unsure"
          intent="unsure"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </VerdictButton>
        {row.savedId !== null && !row.dirty && (
          <Check className="ml-1 h-3.5 w-3.5 text-emerald-500" aria-label="saved" />
        )}
      </div>
    </div>
  )
}

function VerdictButton({
  active,
  onClick,
  title,
  intent,
  children,
}: {
  active: boolean
  onClick: () => void
  title: string
  intent: 'agree' | 'disagree' | 'unsure'
  children: React.ReactNode
}) {
  const activeCls =
    intent === 'agree'
      ? 'border-emerald-700 bg-emerald-600/30 text-emerald-200'
      : intent === 'disagree'
        ? 'border-red-800 bg-red-600/30 text-red-200'
        : 'border-slate-700 bg-slate-700/40 text-slate-200'
  const idleCls = 'border-slate-800 bg-slate-900/60 text-slate-500 hover:text-slate-300'
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`flex h-7 w-7 items-center justify-center rounded border transition ${
        active ? activeCls : idleCls
      }`}
    >
      {children}
    </button>
  )
}
